import React, { useState } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { PostHogProvider } from 'posthog-js/react';
import { Router } from 'react-router-dom';
import { history } from './history';

import AppWrapper from './AppWrapper';
import Routes from './routes';
import api, { logoutLink, TrcpProvider } from './lib/trpcClient';
import Auth0Provider from './providers/auth0Provider';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'further-ui/components/Pdf/fonts';
import { httpBatchLink } from '@trpc/client';
import { useAuth0 } from '@auth0/auth0-react';
import { setupAxiosInstance } from 'lib/httpClient';
import SuperJSON from 'superjson';
import { CalendarDay, CalendarTime } from 'further-ui/utils';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import LogRocket from 'logrocket';
import { maskRequestBody } from 'utils/tracking';

SuperJSON.registerCustom<CalendarDay, string>(
  {
    isApplicable: (v): v is CalendarDay => v instanceof CalendarDay,
    serialize: (v) => v.toString(),
    deserialize: (v) => new CalendarDay(v),
  },
  'CalendarDay',
);

SuperJSON.registerCustom<CalendarTime, string>(
  {
    isApplicable: (v): v is CalendarTime => v instanceof CalendarTime,
    serialize: (v) => v.toString(),
    deserialize: (v) => new CalendarTime(v),
  },
  'CalendarTime',
);

const App: React.FC = () => {
  return (
    <SnackbarProvider
      autoHideDuration={10000}
      maxSnack={5}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      preventDuplicate={true}
    >
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY ?? ''}
        options={{
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          defaults: '2025-11-30',
          session_recording: {
            maskCapturedNetworkRequestFn: (request) => ({
              ...request,
              requestBody: maskRequestBody(request.requestBody ?? ''),
            }),
          },
        }}
      >
        <Router history={history}>
          <Auth0Provider history={history}>
            <AppInner />
          </Auth0Provider>
        </Router>
      </PostHogProvider>
    </SnackbarProvider>
  );
};

const AppInner: React.FC = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [queryClient] = useState(() => {
    setupAxiosInstance(getAccessTokenSilently, loginWithRedirect, history);
    return new QueryClient({
      defaultOptions: {
        queries: {
          placeholderData: keepPreviousData,
          staleTime: QUERY_STALE_TIME_MS,
        },
      },
    });
  });
  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        logoutLink,
        httpBatchLink({
          url: `${process.env.REACT_APP_API_URL}/v2/`,
          transformer: SuperJSON,
          maxURLLength: 10000,
          fetch: (url, options) =>
            window.fetch(url, { ...(options ?? {}), credentials: 'include' }),
          headers: async () => {
            const organization = localStorage.getItem('organizationId');
            let token = '';
            try {
              token = await getAccessTokenSilently({
                authorizationParams: organization
                  ? { organization }
                  : undefined,
              });
            } catch (_error) {
              // The point of this is for the case where a user is currently in Org A and gets removed from it, and their access_token
              // has expired. There will be a call to get a new token (getToken above) but will throw an error because the user is no
              // longer a member of Org A, so then we need to redirect the user through the auth0 login process where they can
              // choose a new org. There's no auth0 native way to handle this right now, so we need to do it manually.
              localStorage.removeItem('organizationId');
              loginWithRedirect({
                authorizationParams: { organization: undefined },
                appState: { returnTo: '/' },
              });
            }

            return {
              authorization: token ? `Bearer ${token}` : '',
              ...(LogRocket.sessionURL && {
                'x-logrocket-url': LogRocket.sessionURL,
              }),
            };
          },
        }),
      ],
    }),
  );

  return (
    <TrcpProvider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AppWrapper>
          <Routes />
        </AppWrapper>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </TrcpProvider>
  );
};
export default App;
